import { Flex } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Avatar, useMantineTheme } from "@mantine/core";
import { motion } from "framer-motion";
import { ComponentProps, FC, useMemo } from "react";
import { useUsers } from "../../../hooks/api/karl-farm/useUsers";
import { useLocale } from "../../../hooks/useLocale";
import { getUserColor } from "../../../utils/getUserColor";
import { UserAvatar } from "../../UserAvatar";
import { useColorTasksPerAssiciatedUsers } from "../storageHooks";
import { CalendarTask } from "./Calendar";

const CalendarItemWrapper = styled(motion.button)`
  position: absolute;

  border-radius: 6px;
  padding: 4px 6px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 3px;

  text-align: left; // Overrides default button text-align

  cursor: pointer;
  touch-action: none;

  overflow: hidden;
  > * {
    flex-shrink: 0;
  }
`;

const CalendarItemTitleContainer = styled.div`
  min-width: 100%;
  max-width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
`;

const CalendarItemTitle = styled.span<{ large: boolean }>`
  max-width: 100%;

  font-size: 14px;
  font-weight: 600;
  line-height: 1.1em;

  white-space: nowrap;
  ${({ large }) =>
    large &&
    `
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

const CalendarItemText = styled.span`
  font-size: 12px;
  line-height: 1.1em;

  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type ConditionIconProps = {
  color: string;
};
const ConditionIcon = styled.div<ConditionIconProps>`
  padding: 5px;
  background-color: ${({ color }) => color};
  border: 1px solid white;
  border-radius: 50%;
`;

type CalendarItemProps = ComponentProps<typeof CalendarItemWrapper> & {
  task: CalendarTask;
  conditionIconColor: string;
  isHighlighted?: boolean;
};
export const CalendarItem: FC<CalendarItemProps> = ({
  task,
  conditionIconColor,
  isHighlighted,
  initial,
  animate,
  ...rest
}) => {
  const { locale } = useLocale();

  const start = task.start?.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "numeric",
  });
  const end = task.end?.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "numeric",
  });
  const timeString = `${start} – ${end}`;

  const small = task.duration < 60;
  const large = task.duration >= 90;

  const { data: usersData } = useUsers();
  const associatedUsers = task.userIds.map((userId) => {
    return usersData?.data.users?.find(
      (user) => user.Attributes.sub === userId
    );
  });

  const { value: colorTasksPerAssociatedUsers } =
    useColorTasksPerAssiciatedUsers();

  const theme = useMantineTheme();
  const { color, background, border } = useMemo(() => {
    const borderAlpha = isHighlighted ? 1 : 0.3;

    if (colorTasksPerAssociatedUsers) {
      const firstUser = associatedUsers[0];
      const userColor = getUserColor(firstUser);
      const defaultColor = "black";
      const defaultBackground = "white";
      const lightColorHex = theme.colors[userColor][0];
      const darkColorHex = theme.colors[userColor][9];
      const darkColorRed = parseInt(darkColorHex.slice(1, 3), 16);
      const darkColorGreen = parseInt(darkColorHex.slice(3, 5), 16);
      const darkColorBlue = parseInt(darkColorHex.slice(5, 7), 16);
      return {
        color: userColor ? darkColorHex : defaultColor,
        background: userColor ? lightColorHex : defaultBackground,
        border: `2px solid rgba(${darkColorRed}, ${darkColorGreen}, ${darkColorBlue}, ${borderAlpha})`,
      };
    } else {
      return {
        color: "hsl(0,0%,10%)",
        background: "hsl(0,0%,97%)",
        border: `2px solid hsla(0, 0%, 50%, ${borderAlpha})`,
      };
    }
  }, [
    associatedUsers,
    colorTasksPerAssociatedUsers,
    isHighlighted,
    theme.colors,
  ]);

  return (
    <CalendarItemWrapper
      initial={{
        border,
        color,
        background,
        ...(typeof initial === "object" ? initial : {}),
      }}
      animate={{
        border,
        color,
        background,
        ...(typeof animate === "object" ? animate : {}),
      }}
      {...rest}
    >
      <CalendarItemTitleContainer>
        <CalendarItemTitle large={!small}>{task.title}</CalendarItemTitle>
        {task.task !== "other" ? (
          <ConditionIcon color={conditionIconColor} />
        ) : null}
        {small && <CalendarItemText>{timeString}</CalendarItemText>}
      </CalendarItemTitleContainer>
      {!small && (
        <>
          <CalendarItemText>{timeString}</CalendarItemText>
          <CalendarItemText>{task.location}</CalendarItemText>
          {large && (
            <Flex marginTop="auto">
              <Avatar.Group>
                {associatedUsers?.map((user, i) => (
                  <UserAvatar
                    key={user?.Attributes.sub || i}
                    user={user}
                    size="sm"
                  />
                ))}
              </Avatar.Group>
            </Flex>
          )}
        </>
      )}
    </CalendarItemWrapper>
  );
};
